import type React from "react";
import { useState } from "react";
import { Box, Button } from "@mui/material";
import { Facebook, WhatsApp, Edit } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { IStreamDetails } from "../../../types/streams";
import { RootState } from "../../../store/Index";
import TemplateSelectorDialog from "../components/memorial-page/components/shared-ui/TemplateSelectorDialog";
import { BaseURL } from "../../../service/Api";
import NewFeatureBadge from "../components/NewFeature";

interface StreamHeaderProps {
  stream: IStreamDetails;
  onCreateThumbnail: () => void;
  onOpenChannelSelectModal: () => void;
  onCreateYouTubeLink: () => void;
  onSocialShare: (platform: "facebook" | "whatsapp") => void;
}

const StreamHeader: React.FC<StreamHeaderProps> = ({
  stream,
  onCreateThumbnail,
  onCreateYouTubeLink,
  onSocialShare,
  onOpenChannelSelectModal,
}) => {
  const navigate = useNavigate();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const isStaging = BaseURL.includes("staging.muzukuru.com");
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);

  const createSlug = (text: string) => {
    return text
      .toLowerCase()
      .trim()
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "");
  };

  const buildMemorialUrl = () => {
    const id = stream.booking.id;
    const serviceType = stream.booking?.service;

    let slugSource = "";
    let pageType = "";

 
    const normalisedServiceType = (serviceType || "")
      .replace(/[_\s-]/g, "")
      .toLowerCase();

    if (
      normalisedServiceType === "funeral" ||
      normalisedServiceType === "funeralhome"
    ) {
      const deceased = stream.booking?.deceased?.[0];
      slugSource = [
        deceased?.first_name,
        deceased?.second_name,
        deceased?.surname,
      ]
        .filter(Boolean)
        .join(" ");
      pageType = "memorial-page";
    } else if (serviceType === "Church") {
      slugSource = stream.title || "church-service";
      pageType = "church-page";
    } else if (serviceType === "Conference") {
      slugSource = stream.title || "conference";
      pageType = "conference-page";
    } else {
      slugSource = "service";
      pageType = "service-page";
    }

    return `/${pageType}/${id}/${encodeURIComponent(createSlug(slugSource))}`;
  };

  const handleNavigateToMemorial = () => {
    const serviceType = stream.booking?.service;
    if (!isStaging || serviceType === "Funeral_Home") {
      navigate(buildMemorialUrl());
      return;
    }
    if (stream.template_id) {
      navigate(buildMemorialUrl());
    } else {
      setTemplateDialogOpen(true);
    }
  };

  const buttonStyle = {
    border: `2px solid ${reduxTheme.primary_colors[100]}`,
    color: reduxTheme.primary_colors[100],
    px: 1.5,
    py: 0.7,
  };

  const handleCreateYouTubeLink = () => {
    onOpenChannelSelectModal();
    onCreateYouTubeLink();
  };
  const serviceType = stream.booking?.service;

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        mb: 3,
        flexWrap: "wrap",
        gap: 1,
      }}
    >
      <Button variant="outlined" sx={buttonStyle} onClick={onCreateThumbnail}>
        Create Thumbnail
      </Button>
      <Button
        variant="outlined"
        sx={buttonStyle}
        onClick={handleCreateYouTubeLink}
      >
        Create YouTube Link
      </Button>
      <Button
        variant="outlined"
        sx={buttonStyle}
        onClick={handleNavigateToMemorial}
      >
        {serviceType === "Church" ||
        serviceType === "Other" ||
        serviceType === "Conference"
          ? "Stream Page"
          : "Memorial Page"}
      </Button>
      <Button
        variant="outlined"
        sx={buttonStyle}
        onClick={() =>
          navigate(`/stream-management/view/${stream.id}/callsheet`)
        }
      >
        Callsheet
      </Button>
      {serviceType !== "Church" && (
        <Button
          variant="outlined"
          sx={buttonStyle}
          onClick={() =>
            navigate(`/stream-management/view/${stream.id}/table-tent`)
          }
        >
          Table Tent
        </Button>
      )}

      <NewFeatureBadge feature="cost-analysis">
        <Button
          variant="outlined"
          sx={buttonStyle}
          onClick={() =>
            navigate(`/stream-management/view/${stream.id}/cost-analysis`)
          }
        >
          Cost Analysis
        </Button>
      </NewFeatureBadge>

      <NewFeatureBadge feature="stream-report">
        <Button
          variant="outlined"
          sx={buttonStyle}
          onClick={() => {
            const svcType = stream.booking?.service;
            const isFuneral =
              svcType === "Funeral" || svcType === "FuneralHome";
            const deceased = stream.booking?.deceased?.[0];
            const deceasedFullName = isFuneral
              ? [deceased?.first_name, deceased?.second_name, deceased?.surname]
                  .filter(Boolean)
                  .join(" ") || "N/A"
              : null;
            navigate(`/stream-management/view/${stream.id}/stream-report`, {
              state: {
                serviceType: svcType,
                deceasedName: deceasedFullName,
                streamTitle: stream.title || null,
              },
            });
          }}
        >
          Stream Report
        </Button>
      </NewFeatureBadge>
      <NewFeatureBadge feature="financial-report">
        <Button
          variant="outlined"
          sx={buttonStyle}
          onClick={() =>
            navigate(`/stream-management/view/${stream.id}/financial-report`)
          }
        >
          Financial Report
        </Button>
      </NewFeatureBadge>
      <Button
        startIcon={<Facebook />}
        sx={{
          backgroundColor: "#1877F2",
          color: reduxTheme.primary_colors[900],
          px: 1.5,
          py: 1,
          "&:hover": { backgroundColor: "#0f65d4" },
        }}
        onClick={() => onSocialShare("facebook")}
      >
        Share
      </Button>
      <Button
        startIcon={<WhatsApp />}
        sx={{
          backgroundColor: "#25d366",
          color: reduxTheme.primary_colors[900],
          px: 1.5,
          py: 1,
          "&:hover": { backgroundColor: "#1da851" },
        }}
        onClick={() => onSocialShare("whatsapp")}
      >
        Share
      </Button>
      <Button
        variant="outlined"
        startIcon={<Edit />}
        sx={{
          backgroundColor: reduxTheme.primary_colors[400],
          color: reduxTheme.primary_colors[900],
          px: 1.5,
          py: 1,
        }}
        onClick={() => navigate(`/stream-management/view/${stream.id}/edit`)}
      >
        Edit
      </Button>

      {/* {isStaging && ( */}
      <TemplateSelectorDialog
        open={templateDialogOpen}
        onClose={() => setTemplateDialogOpen(false)}
        serviceType={stream.booking?.service ?? "Funeral"}
        streamId={stream.id}
        currentTemplateId={stream.template_id}
        onConfirm={() => navigate(buildMemorialUrl())}
      />
      {/* )} */}
    </Box>
  );
};

export default StreamHeader;
